
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Poll } from '@/types/poll';
import { useAuth } from '@/contexts/auth';
import { usePollUpdater } from './usePollUpdater';
import { useCardUpdater } from './useCardUpdater';
import { useVotePermissions } from './useVotePermissions';

export const usePollVoting = (
  polls: Poll[],
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>
) => {
  const { user } = useAuth();
  const [isVoting, setIsVoting] = useState<Record<string, boolean>>({});
  const { fetchUpdatedPoll } = usePollUpdater();
  const { updateCard } = useCardUpdater();
  const { canVote, cannotVoteReason } = useVotePermissions();
  
  const handleVotePoll = async (pollId: string, optionId: number) => {
    if (!user) {
      toast.error('You must be signed in to vote');
      return;
    }
    
    if (isVoting[pollId]) return; // Prevent double votes
    
    try {
      // Set voting flag for this poll
      setIsVoting(prev => ({ ...prev, [pollId]: true }));
      
      console.log(`Submitting vote for poll ${pollId}, option ${optionId}`);
      
      // Insert vote record
      const { error: voteError } = await supabase
        .from('poll_votes')
        .insert({
          poll_id: pollId,
          user_id: user.id,
          option_id: optionId
        });
      
      if (voteError) {
        throw voteError;
      }
      
      toast.success('Your vote has been recorded');
      
      // Fetch updated poll data
      const updatedPollData = await fetchUpdatedPoll(pollId);
      
      if (updatedPollData) {
        // Update local poll state
        setPolls(currentPolls =>
          currentPolls.map(poll =>
            poll.id === pollId
              ? {
                  ...poll,
                  ...updatedPollData,
                  results: {
                    ...poll.results,
                    selectedOptionId: optionId,
                    totalVotes: updatedPollData.total_votes || poll.total_votes
                  }
                }
              : poll
          )
        );
        
        // Find the poll to get the linked card ID
        const votedPoll = polls.find(p => p.id === pollId);
        
        // Update the linked card if it exists
        if (votedPoll && votedPoll.linked_card_id) {
          console.log(`Poll ${pollId} has linked card ${votedPoll.linked_card_id}, updating card stats...`);
          await updateCard({...votedPoll, ...updatedPollData});
          
          // Log to help diagnose if card update is happening
          console.log(`Card update triggered for card ${votedPoll.linked_card_id}`);
        }
      }
    } catch (error) {
      console.error('Error voting on poll:', error);
      toast.error('Failed to submit your vote');
    } finally {
      // Clear voting flag
      setIsVoting(prev => ({ ...prev, [pollId]: false }));
    }
  };

  return {
    handleVotePoll,
    isVoting,
    canVote,
    cannotVoteReason
  };
};
